import { PATHS } from "./paths";

export interface NavItem {
  name: string; // Original style property
  label: string;
  path: string;
  icon: string;
  slug: string; // Used to match backend permissions
  subItems?: NavItem[];
}

/**
 * Unified pool of all available navigation items in the system.
 * This list is filtered dynamically based on backend permissions.
 */
export const APP_NAVIGATION: NavItem[] = [
  {
    name: "Dashboard",
    label: "Dashboard",
    path: PATHS.DASHBOARD,
    icon: "Home",
    slug: "dashboard"
  },

  {
    name: "Lead Management",
    label: "Lead Management",
    path: PATHS.LEAD_TRACKING,
    icon: "UserPlus",
    slug: "lead-management",
    subItems: [
      { name: "Campaign Dashboard", label: "Campaign Dashboard", path: PATHS.CAMPAIGN_DASHBOARD, icon: "BarChart2", slug: "campaign-dashboard" },
      { name: "Lead Tracking", label: "Lead Tracking", path: PATHS.LEAD_TRACKING, icon: "Activity", slug: "lead-tracking" },
      { name: "Activities", label: "Activities", path: PATHS.LEAD_ACTIVITIES, icon: "Calendar", slug: "lead-activities" },
      { name: "Accounts", label: "Accounts", path: PATHS.LEAD_ACCOUNTS, icon: "ShoppingBag", slug: "lead-accounts" },
      { name: "Contact", label: "Contact", path: PATHS.LEAD_CONTACT, icon: "Contact", slug: "lead-contact" },
      { name: "Lost Lead", label: "Lost Lead", path: PATHS.LEAD_LOST, icon: "XCircle", slug: "lead-lost" },
      { name: "All Leads", label: "All Leads", path: PATHS.LEAD_ALL, icon: "List", slug: "lead-all" },
      { name: "Auto Dialer", label: "Auto Dialer", path: PATHS.AUTO_DIALER, icon: "Phone", slug: "auto-dialer" },
      { name: "Lead Report", label: "Lead Report", path: PATHS.LEAD_REPORTS, icon: "BarChart", slug: "lead-report" },
    ]
  },

  {
    name: "Social Media Manager",
    label: "Social Media Manager",
    path: PATHS.SOCIAL_DASHBOARD,
    icon: "Layers",
    slug: "social-media",
    subItems: [
      { name: "Analytics", label: "Analytics", path: PATHS.SOCIAL_DASHBOARD, icon: "BarChart3", slug: "social-dashboard" },
      { name: "Lead Manager", label: "Lead Manager", path: PATHS.SOCIAL_LEADS, icon: "List", slug: "social-leads" },
      { name: "Kanban Board", label: "Kanban Board", path: PATHS.SOCIAL_KANBAN, icon: "Layout", slug: "social-kanban" },
      { name: "Integrations", label: "Integrations", path: PATHS.SOCIAL_INTEGRATIONS, icon: "Link", slug: "social-integrations" },
    ]
  },

  {
    name: "Access Control",
    label: "Access Control",
    path: PATHS.ACCESS_CONTROL,
    icon: "Shield",
    slug: "access-control"
  },

  {
    name: "Security & Audit",
    label: "Security & Audit",
    path: PATHS.SECURITY_SESSIONS,
    icon: "ShieldCheck",
    slug: "security-audit",
    subItems: [
      { name: "Sessions", label: "Sessions", path: PATHS.SECURITY_SESSIONS, icon: "Monitor", slug: "security-audit" },
      { name: "Audit Timeline", label: "Audit Timeline", path: PATHS.SECURITY_AUDIT, icon: "History", slug: "security-audit" },
      { name: "Protection Settings", label: "Protection Settings", path: PATHS.SECURITY_PROTECTION, icon: "AlertCircle", slug: "security-audit" },
    ]
  },


  {
    name: "Manage Client",
    label: "Manage Client",
    path: PATHS.CLIENT_MANAGEMENT,
    icon: "Users",
    slug: "manage-client",
    subItems: [
      { name: "All Clients", label: "All Clients", path: PATHS.CLIENT_MANAGEMENT, icon: "List", slug: "manage-client" },
      { name: "Add Client", label: "Add Client", path: PATHS.ADD_CLIENT, icon: "Plus", slug: "add-client" },
    ]
  },

  {
    name: "Manage Customer",
    label: "Manage Customer",
    path: PATHS.CUSTOMER_MANAGEMENT,
    icon: "Users",
    slug: "manage-customer",
    subItems: [
      { name: "All Customers", label: "All Customers", path: PATHS.CUSTOMER_MANAGEMENT, icon: "List", slug: "manage-customer" },
      { name: "Add Customer", label: "Add Customer", path: PATHS.ADD_CUSTOMER, icon: "Plus", slug: "add-customer" },
    ]
  },

  {
    name: "Courses",
    label: "Courses",
    path: PATHS.COURSE_MANAGEMENT,
    icon: "GraduationCap",
    slug: "courses",
    subItems: [
      { name: "All Courses", label: "All Courses", path: PATHS.COURSE_MANAGEMENT, icon: "BookOpen", slug: "edit-courses" },
      { name: "Add Course", label: "Add Course", path: PATHS.ADD_COURSE, icon: "PlusCircle", slug: "add-courses" },
    ]
  },

  {
    name: "Manage Attendance",
    label: "Manage Attendance",
    path: PATHS.VIEW_ATTENDANCE,
    icon: "ClipboardCheck",
    slug: "manage-attendance",
    subItems: [
      { name: "View History", label: "View History", path: PATHS.VIEW_ATTENDANCE, icon: "History", slug: "view-attendance" },
      { name: "Mark Attendance", label: "Mark Attendance", path: PATHS.MARK_ATTENDANCE, icon: "UserCheck", slug: "mark-attendance" },
    ]
  },

  {
    name: "Library",
    label: "Library",
    path: PATHS.VIEW_LIBRARY,
    icon: "Library",
    slug: "library",
    subItems: [
      { name: "View Books", label: "View Books", path: PATHS.VIEW_LIBRARY, icon: "Book", slug: "view-library" },
      { name: "Add Book", label: "Add Book", path: PATHS.ADD_LIBRARY, icon: "Plus", slug: "add-library" },
    ]
  },

  {
    name: "Leaves",
    label: "Leaves",
    path: PATHS.LEAVE_MANAGEMENT,
    icon: "Calendar",
    slug: "leaves",
    subItems: [
      { name: "View Leaves", label: "View Leaves", path: PATHS.LEAVE_MANAGEMENT, icon: "Eye", slug: "view-leave" },
      { name: "Apply Leave", label: "Apply Leave", path: PATHS.ADD_LEAVE, icon: "FilePlus", slug: "add-leave" },
    ]
  },

  {
    name: "Fees",
    label: "Fees",
    path: PATHS.STUDENT_FEES,
    icon: "DollarSign",
    slug: "fees",
    subItems: [
      { name: "Fee Payment", label: "Fee Payment", path: PATHS.STUDENT_FEES, icon: "CreditCard", slug: "fees" },
    ]
  },

  {
    name: "User Management",
    label: "User Management",
    path: PATHS.USER_MANAGEMENT,
    icon: "Users",
    slug: "users"
  },

  {
    name: "Salary Payout",
    label: "Salary Payout",
    path: PATHS.SALARY_PAYOUT,
    icon: "Banknote",
    slug: "salary-payout",
    subItems: [
      { name: "Payroll Run", label: "Payroll Run", path: PATHS.PAYROLL_RUN, icon: "PlayCircle", slug: "payroll-run" },
      { name: "Assignment", label: "Assignment", path: PATHS.SALARY_ASSIGNMENT, icon: "UserPlus", slug: "salary-assignment" },
      { name: "Configuration", label: "Configuration", path: PATHS.SALARY_CONFIGURATION, icon: "Settings", slug: "salary-configuration" },
    ]
  },

  {
    name: "Reports",
    label: "Reports",
    path: PATHS.REPORTS,
    icon: "FileBarChart",
    slug: "reports",
    subItems: [
      { name: "NRE Report", label: "NRE Report", path: PATHS.REPORTS, icon: "FileText", slug: "nre-report" },
    ]
  }
];

// Legacy CONFIG support for backward compatibility during transition
export const NAVIGATION_CONFIG: Record<string, NavItem[]> = {
  SUPERADMIN: APP_NAVIGATION,
  // Others will now be handled dynamically via APP_NAVIGATION + backend slugs
};
